/**
 * groupnav.context.ts
 * Secured Communications
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import * as React from "react";

import { AccountScopes, PermissionLevel } from "../models/auth";
import { Group, GroupMembership, GroupScopes } from "../models/group";
import { AccountMemberMention } from "../editor/scEditor";
import { RatchetTree } from "../models/crypto";

export interface GroupNavContext {
  readonly currentGroup: GroupMembership | null,
  readonly huddleGroup: GroupMembership | null,
  readonly groupMemberships: Array<GroupMembership>,
  readonly directMessages: Array<GroupMembership>,
  readonly groupMembershipMap: { [groupID: string]: GroupMembership},
  setCurrentGroup(group: GroupMembership | null): Promise<RatchetTree | null>,
  finishAddGroup(group: Group): Promise<[RatchetTree | null, GroupMembership | null]>,
  addThisDeviceToGroup(groupID: string): Promise<any>,
  recoverGroup(groupID: string): Promise<void>,
  setThread(threadID: string): Promise<void>,
  toggleGroupList(): void,
  toggleRightDetail(): void,
  getMatchingMembers (queryText: string, groupID: string): Promise<Array<AccountMemberMention>>,
  mainScrollingElement(): HTMLElement | null,
  navTo(url: string, leaveRight?: boolean): void,
  doHuddle(groupID: string): void,
  didJoinHuddle(groupID: string): void,
  leaveHuddle(): Promise<void>,
  enableArchiving(groupID: string): Promise<void>,
  can(level: PermissionLevel, perm: keyof GroupScopes, groupID?: string, acctPerm?: keyof AccountScopes): boolean
}

/* eslint-disable @typescript-eslint/no-unused-vars */
export const GroupNavContext: React.Context<GroupNavContext> = React.createContext({
  currentGroup: null as GroupMembership | null,
  huddleGroup: null as GroupMembership | null,
  groupMemberships: [] as Array<GroupMembership>,
  directMessages: [] as Array<GroupMembership>,
  groupMembershipMap: {} as { [groupID: string]: GroupMembership},
  setCurrentGroup: async (_group: GroupMembership | null): Promise<RatchetTree | null> => { return null; },
  finishAddGroup: async (_group: Group): Promise<[RatchetTree | null, GroupMembership | null]> => { return [null, null]; },
  addThisDeviceToGroup: (async () => {}) as (groupID: string) => Promise<any>,
  recoverGroup: (async () => {}) as (groupID: string) => Promise<void>,
  setThread: (async () => {}) as (threadID: string) => Promise<void>,
  toggleGroupList: () => {},
  toggleRightDetail: () => {},
  getMatchingMembers: (async (): Promise<Array<AccountMemberMention>> => []) as (queryText: string, groupID: string) => Promise<Array<AccountMemberMention>>,
  mainScrollingElement: (): HTMLElement | null => { return null; },
  navTo: (_url: string, _leaveRight = false): void => {},
  doHuddle: (_groupID: string): void => {},
  didJoinHuddle: (_groupID: string): void => {},
  leaveHuddle: async (): Promise<void> => {},
  enableArchiving: async (_groupID: string): Promise<void> => {},
  can: (_level: PermissionLevel, _perm: keyof GroupScopes): boolean => { return false; }
});
/* eslint-enable @typescript-eslint/no-unused-vars */
